// At-Risk Pros Command Center data layer — wraps the generated
// `@sidegigllc/api/client` hooks (CORE-5988 annotated the OpenAPI schema) and
// keeps the FE-only filter/sort/cohort concepts the components drive off.

import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import {
  type AdminMatchDashboardAtRiskProsListParams,
  type CohortEnum,
  type PaginatedAtRiskProList,
  adminMatchDashboardAtRiskProsDismissCreate,
  getAdminMatchDashboardAtRiskProsFilterOptionsRetrieveQueryOptions,
  getAdminMatchDashboardAtRiskProsListInfiniteQueryKey,
  getAdminMatchDashboardAtRiskProsListInfiniteQueryOptions,
  getAdminMatchDashboardAtRiskProsSummaryRetrieveQueryKey,
  getAdminMatchDashboardAtRiskProsSummaryRetrieveQueryOptions,
} from "@sidegigllc/api/client";
import { type InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";

const AT_RISK_PROS_COHORTS = ["cancellation", "inexperienced", "dormant", "expiring"] as const;
export type AtRiskProCohort = (typeof AT_RISK_PROS_COHORTS)[number];

export type AtRiskProSort = "shift_asc" | "shift_desc" | "name" | "market";

// CORE-6535: outreach-status filter values. `undefined` = "All" (no filter); the three below key off the
// next shift's ProOutreach rollup on the BE. Kept narrower than the generated enum (which also has `all`) —
// the "All" option maps to `undefined` so no `?outreach_status=` is sent, matching the other filters.
export type AtRiskProOutreachStatus = "no_outreach" | "outreach_successful" | "outreach_attempted_no_response";

/** Core filters (left sidebar) + cohort + sort. `page` lives on the infinite query, not here. */
export interface AtRiskProFilters {
  // CORE-6290 Outreach tab. `undefined` (or true) = the at-risk-only queue; `false` = the Outreach
  // view (every confirmed pro in scope, not just flagged ones — dormant pros drop out server-side).
  at_risk?: boolean;
  risk_cohort?: AtRiskProCohort;
  // Mutable (not ReadonlyArray) so it stays assignable to the typesafe-route's
  // `number().defined().array()` param when spread through `setParams`.
  market_id?: Array<number>;
  location_id?: number;
  skill_id?: number;
  auto_select?: boolean;
  upcoming_days?: number;
  // Both gate on the pro's next shift's location: VIP = location has an active VIP add-on;
  // first-5 = the shift is among the first 5 gigs posted at that location. Dormant pros
  // (no shift) drop out when either is set, same as `location_id` / `skill_id`.
  is_vip?: boolean;
  is_first_5_for_location?: boolean;
  // CORE-6290: pro's next shift is their first-ever confirmed gig (first-time confirmer). Dormant
  // pros drop out when set (no shift). Chiefly an Outreach-tab cohort, but valid on either tab.
  first_confirm?: boolean;
  // CORE-6535: filter by the next shift's outreach state. `undefined` = All. Dormant pros drop out when
  // set (no shift, no outreach), same as location_id / first_confirm. Valid on both tabs.
  outreach_status?: AtRiskProOutreachStatus;
  search?: string;
  sort?: AtRiskProSort;
}

/** Map the sidebar filter shape onto the generated list/summary query params. */
function toParams(filters: AtRiskProFilters): AdminMatchDashboardAtRiskProsListParams {
  return {
    // Omit when true (the BE default) so the at-risk tab sends no `?at_risk=` — only the Outreach
    // tab (false) puts it on the wire.
    at_risk: filters.at_risk === false ? false : undefined,
    // `expiring` never reaches here (no chip), but the FE cohort union is wider than the
    // BE's Phase-1 param enum, so narrow.
    risk_cohort: filters.risk_cohort as AdminMatchDashboardAtRiskProsListParams["risk_cohort"],
    market_id: filters.market_id,
    location_id: filters.location_id,
    skill_id: filters.skill_id,
    auto_select: filters.auto_select,
    upcoming_days: filters.upcoming_days,
    is_vip: filters.is_vip,
    is_first_5_for_location: filters.is_first_5_for_location,
    first_confirm: filters.first_confirm,
    outreach_status: filters.outreach_status,
    search: filters.search,
    ordering: filters.sort,
  };
}

export const atRiskProsListQueryOptions = (filters: AtRiskProFilters) =>
  getAdminMatchDashboardAtRiskProsListInfiniteQueryOptions<InfiniteData<PaginatedAtRiskProList, number>>(
    toParams(filters),
    {
      query: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
      },
    },
  );

export const atRiskProsSummaryQueryOptions = (filters: AtRiskProFilters) => {
  // Summary ignores `risk_cohort` (each chip count is within the current core-filter context).
  const { risk_cohort: _ignored, sort: _ignoredSort, ...core } = filters;
  return getAdminMatchDashboardAtRiskProsSummaryRetrieveQueryOptions(toParams(core));
};

export const atRiskProsFilterOptionsQueryOptions = (atRisk?: boolean) =>
  // Options span the current population (at-risk-only vs the wider Outreach set). Omit when true
  // so the at-risk tab sends no `?at_risk=`.
  getAdminMatchDashboardAtRiskProsFilterOptionsRetrieveQueryOptions({ at_risk: atRisk === false ? false : undefined });

export interface DismissAtRiskProVariables {
  proId: number;
  /** null = dismiss the pro from every cohort. */
  cohort: CohortEnum | null;
}

export function useDismissAtRiskPro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proId, cohort }: DismissAtRiskProVariables) =>
      adminMatchDashboardAtRiskProsDismissCreate(proId, { cohort }),
    onSuccess: () => {
      // Dismissed pros drop out of both the list and the chip counts.
      void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardAtRiskProsListInfiniteQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardAtRiskProsSummaryRetrieveQueryKey() });
    },
    // Surface failures (the row stays put + spinner stops) — matches the toast-on-error
    // convention of the other admin mutations.
    onError: (error) => toast.error(ApiError.fromThrown(error).getMainErrorMessage() ?? "Failed to dismiss pro"),
  });
}
